<template>
	<div class="calendar">
		<div style="position: sticky; top:12px; z-index: 200;">
			<Breadcrumbs :items="items" />
		</div>
		<v-container fluid class="mt-2">
			<v-row no-gutters>
				<v-btn color="primary" text small @click="showFilters = !showFilters">
					<translate>Filters</translate>
					<v-icon v-if="showFilters">mdi-chevron-up</v-icon>
					<v-icon v-else>mdi-chevron-down</v-icon>
				</v-btn>
				<v-spacer></v-spacer>
				<template v-if="$store.getters.hasRole('manager')">
				<v-btn small v-if="!$store.getters.googleLogged" @click="loginGoogle" :loading="googleLogging">
					<v-icon small color="primary" class="mr-2">mdi-google</v-icon>
					Sincronizar
				</v-btn>
				<v-btn small v-else @click="showGoogleSettings = true; getGoogleCalendars()">
					<v-icon small color="primary" class="mr-2">mdi-google</v-icon>
					<translate>Settings</translate>
					<v-dialog v-model="showGoogleSettings" max-width="50%" scrollable>
						<v-card :loading="googleLogging">
							<v-card-title class="title primary--text mb-5 secondary">
								<translate>Google Calendar</translate>
							</v-card-title>
							<v-card-text>
								<p v-translate class="text-subtitle-1 mb-1">Used calendars</p>
								<p
									v-if="usedCalendars.length == 0"
									class="font-weight-thin ml-2 my-2"
									v-translate
									key="no-selected"
								>No calendars.</p>
								<v-list v-else flat>
									<v-list-item
										:key="calendar.id"
										style="border: 1px solid #e0e0e0; border-radius: 5px"
										v-for="calendar in usedCalendars"
									>
										<v-list-item-icon class="mr-3">
											<v-icon :color="calendar.backgroundColor">mdi-calendar</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title>
												<strong>{{calendar.summary}}</strong>
											</v-list-item-title>
											<v-list-item-subtitle></v-list-item-subtitle>
										</v-list-item-content>
										<v-list-item-action>
											<v-row no-gutters>
												<v-btn icon @click="currentCalendar = calendar; showCalendarUsers = true" :disabled="googleLogging">
													<v-icon color="primary">person</v-icon>
												</v-btn>
												<v-btn icon @click="removeCalendar(calendar)" :disabled="googleLogging">
													<v-icon color="primary">delete</v-icon>
												</v-btn>
											</v-row>
										</v-list-item-action>
									</v-list-item>
								</v-list>
								<p class="text-subtitle-1 mb-1" v-translate>Other calendars</p>
								<p
									v-if="otherCalendars.length == 0"
									class="font-weight-thin ml-2 my-2"
									v-translate
									key="no-more"
								>No calendars.</p>
								<v-list flat>
									<v-list-item
										:key="calendar.id"
										style="border: 1px solid #e0e0e0; border-radius: 5px"
										v-for="calendar in otherCalendars"
									>
										<v-list-item-icon class="mr-3">
											<v-icon :color="calendar.backgroundColor">mdi-calendar</v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title>
												<strong>{{calendar.summary}}</strong>
											</v-list-item-title>
										</v-list-item-content>
										<v-list-item-action>
											<v-btn icon @click="addCalendar(calendar)" :disabled="googleLogging">
												<v-icon color="primary">add</v-icon>
											</v-btn>
										</v-list-item-action>
									</v-list-item>
								</v-list>
							</v-card-text>
							<v-divider></v-divider>
							<v-card-actions>
								<v-btn color="primary" depressed @click="logoutGoogle" :disabled="googleLogging">
									<v-icon color="secondary" class="mr-1">mdi-google</v-icon>
									Logout
								</v-btn>
								<v-spacer></v-spacer>
								<v-btn color="primary" @click="showAutomaticInfo = true" depressed :disabled="googleLogging">
									<translate>Automatic Association</translate>
									<v-dialog v-model="showAutomaticInfo" width="40%" scrollable>
										<v-card>
											<v-card-title class="title primary--text mb-2 secondary" v-translate>Automatic Association</v-card-title>
											<v-card-text>
												<span>
													<translate>Automatic Association will associate each google event to a effective created appointment, only if the appointment happened at the same time and is associated with the same health professional.</translate>
													<br>
													<translate>Are you sure you want to continue? This action is irreversible.</translate>
												</span>
											</v-card-text>
											<v-divider></v-divider>
											<v-card-actions>
												<v-btn color="primary" text @click="showAutomaticInfo = false"><translate>Cancel</translate></v-btn>
												<v-spacer></v-spacer>
												<v-btn color="primary" text @click="automaticAssociation" :loading="googleLogging"><translate>Associate</translate></v-btn>
											</v-card-actions>
										</v-card>
									</v-dialog>
								</v-btn>
								<v-btn color="primary darken-1" @click="showGoogleSettings = false" text>
									<translate>Close</translate>
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
					<v-snackbar
						v-model="showNumAppointments"
						:timeout="2000"
					>
						Foram associadas {{ numAppointmentsAssociated }} consultas
					</v-snackbar>
					<v-dialog v-model="showCalendarUsers" width="40%" scrollable>
						<v-card>
							<v-card-title class="title primary--text mb-2 secondary" v-translate>Associated User</v-card-title>
							<v-card-text v-if="currentCalendar">
								<v-select
									:items="healthProfessionals"
									:label="$gettext('Health Professional')"
									v-model="currentCalendar.user_id"
									class="mt-2"
									outlined
									:rules="[required]"
									:no-data-text="$gettext('No Health Professionals available')"
									item-text="name"
									item-value="id"
								></v-select>
							</v-card-text>
							<v-divider></v-divider>
							<v-card-actions>
								<v-btn color="primary" text @click="showCalendarUsers = false"><translate>Close</translate></v-btn>
								<v-spacer></v-spacer>
								<v-btn color="primary" text @click="saveTerapeut"><translate>Save</translate></v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</v-btn>
				</template>
			</v-row>
			<template v-if="showFilters">
				<v-btn-toggle style="display: block" v-model="establishmentFilter" @change="getAppointments" class="mt-1">
					<v-btn value="all" small class="my-1 text-overline">
						<v-icon small color="primary" class="mr-2">mdi-home-city-outline</v-icon>
						<translate>All</translate>
					</v-btn>
					<v-btn :value="e.id" small v-for="e in establishments" :key="e.id" class="my-1 text-overline">
						<v-icon small :color="e.color" class="mr-1">mdi-domain</v-icon>
						{{e.name}}
					</v-btn>
				</v-btn-toggle>
				<v-btn-toggle style="display: block" v-model="userFilter" @change="getAppointments">
					<v-btn value="all" small class="my-1 text-overline">
						<v-icon small color="primary" class="mr-2">people</v-icon>
						<translate>All</translate>
					</v-btn>
					<v-btn :value="hp.id" small v-for="hp in healthProfessionals" :key="hp.id" class="my-1 text-overline">
						<v-icon small :color="hp.color" class="mr-1">person</v-icon>
						{{hp.name}}
					</v-btn>
				</v-btn-toggle>
			</template>
			<v-row class="fill-height">
				<v-col>
					<v-sheet class="calendar-toolbar">
						<v-toolbar flat color="white">
							<v-btn outlined small class="mr-4" color="grey darken-2" @click="setToday">
								<translate>Today</translate>
							</v-btn>
							<v-btn fab text small color="grey darken-2" @click="prev">
								<v-icon small>mdi-chevron-left</v-icon>
							</v-btn>
							<v-btn fab text small color="grey darken-2" @click="next">
								<v-icon small>mdi-chevron-right</v-icon>
							</v-btn>
							<v-toolbar-title>
								<span style="color: grey; font-size: 15px">{{ title }}</span>
							</v-toolbar-title>
							<v-spacer></v-spacer>
							<v-menu bottom right>
								<template v-slot:activator="{ on }">
									<v-btn small outlined color="grey darken-2" v-on="on">
										<span>{{ typeToLabel[type] }}</span>
										<v-icon right>mdi-menu-down</v-icon>
									</v-btn>
									<v-btn
										v-if="$store.getters.canCreateAppointment"
										small
										color="primary"
										:disabled="!$store.getters.hasAnyModule"
										class="mr-1"
										@click="openNewAppointment"
									>
										<v-icon small class="mr-1">add</v-icon>
										<translate>New Appointment</translate>
									</v-btn>
								</template>
								<v-list class="pa-0">
									<v-list-item dense @click="type = 'day'">
										<v-list-item-title>
											<translate>Day</translate>
										</v-list-item-title>
									</v-list-item>
									<v-list-item dense @click="type = 'week'">
										<v-list-item-title>
											<translate>Week</translate>
										</v-list-item-title>
									</v-list-item>
									<v-list-item dense @click="type = 'month'">
										<v-list-item-title>
											<translate>Month</translate>
										</v-list-item-title>
									</v-list-item>
									<v-list-item dense @click="type = '4day'">
										<v-list-item-title>
											4
											<translate>Days</translate>
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-toolbar>
					</v-sheet>
					<v-sheet>
						<v-calendar
							v-model="focus"
							ref="calendar"
							color="primary"
							:events="appointments"
							:event-color="getEventColor"
							:event-name="getEventName"
							:now="now"
							:type="type"
							@click:event="showAppointment"
							@click:more="viewDay"
							@click:time="newAppointment"
							@change="updateRange"
							interval-minutes="30"
							:first-interval="firstInterval"
							interval-height="25"
							:interval-format="intervalFormat"
							:month-format="monthFormat"
							:weekday-format="weekDayFormat"
							:weekdays="weekdays"
							locale="WET"
							event-overlap-mode="column"
							:event-overlap-threshold="10"
							event-ripple
							:interval-count="intervalCount"
						>
							<template v-slot:event="event">
								<span class="ml-1">{{event.event.name}}</span>
								<v-icon v-if="event.event.google" style="position: absolute; right: 2px; bottom: 2px" color="white" small>mdi-google</v-icon>
								<v-icon v-if="event.event.missed" style="position: absolute; right: 2px; bottom: 2px" color="white" small>mdi-account-remove</v-icon>
							</template>
						</v-calendar>
						<v-dialog v-model="selectedOpen" width="1000" :activator="selectedElement" scrollable>
							<v-card>
								<v-card-title class="title primary--text mb-2 secondary">
									<translate>Appointment</translate>
									<template v-if="!newApp && (!appointment.effective || $store.getters.hasRole('manager'))">
										<v-spacer></v-spacer>
										<v-btn small v-if="appointment.missed" color="primary" class="mr-3" depressed @click="missedOpen = true" :disabled="!$store.getters.hasAnyModule">
											<v-icon small class="mr-1">mdi-account-remove</v-icon>
											<translate key="missed">Missed</translate>
										</v-btn>
										<v-dialog v-model="missedOpen" width="40%">
											<v-card>
												<v-card-title class="title primary--text mb-2 secondary">
													<translate>Missed Reason</translate>
												</v-card-title>
												<v-card-text>
													<v-textarea
														v-model="appointment.missed_reason"
														filled
														class="text-body-2"
														placeholder="..."
														auto-grow
														persistent-placeholder
														hide-details
													/>
												</v-card-text>
												<v-divider></v-divider>
												<v-card-actions>
													<v-btn color="secondary" class="primary--text" depressed @click="missedOpen = false">
														<translate key="cancel">Cancel</translate>
													</v-btn>
													<v-spacer></v-spacer>
													<v-btn v-if="appointment.missed" color="secondary" class="primary--text" depressed @click="saveAppointment(false, false, {use: true, value: false})">
														<translate key="remove-miss">Remove Missed</translate>
													</v-btn>
													<v-btn color="secondary" class="primary--text" depressed @click="saveAppointment(false, false, {use: true, value: true})">
														<translate v-if="appointment.missed" key="save">Save</translate>
														<translate v-else key="missed">Missed</translate>
													</v-btn>
												</v-card-actions>
											</v-card>
										</v-dialog>
										<v-btn icon @click="showDeleteAppointment = true" v-on="on">
											<v-icon color="primary">delete</v-icon>
											<DeleteConfirmationDialog v-model="showDeleteAppointment" item="appointment" @delete="deleteAppointment" />
										</v-btn>
									</template>
								</v-card-title>
								<v-card-text>
									<NewAppointmentForm
										:calendar="true"
										:appointment="appointment"
										:patients="patients"
										:professionals="healthProfessionals"
										:specialities="specialities"
										:appointmentTypes="appointmentTypes"
										:appointmentLocations="appointmentLocations"
										ref="AppointmentForm"
										@edited="edited = true"
									/>
									<v-row no-gutters v-if="appointment.google">
										<v-icon small color="primary" class="mr-2">mdi-google</v-icon>
										<span>{{appointment.name}}</span>
									</v-row>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-actions>
									<v-btn color="secondary" class="primary--text" depressed @click="selectedOpen = false">
										<translate key="cancel">Cancel</translate>
									</v-btn>
									<v-btn v-if="!newApp && appointment.patient_id && appointment.patient_id != -1 && ownAppointment" :to="{name: 'patient', params: {pid: appointment.patient_id, tab: 'clinic'}}" color="secondary" class="primary--text" depressed>
										<translate key="clinic-history">Clinic H.</translate>
									</v-btn>
									<v-spacer></v-spacer>
									<template v-if="!newApp">
										<v-btn v-if="currentEvent" color="secondary" class="primary--text" @click="associateAppointment" depressed :disabled="!$store.getters.hasAnyModule">
											<translate key="confirm-association">Confirm Association</translate> <span class="mx-1">-</span>{{currentEvent.name}}
										</v-btn>
										<v-btn v-if="ownAppointment && !appointment.missed" :color="$store.getters.tourMode ? 'primary' : 'secondary'" :class="$store.getters.tourMode ? 'white--text' : 'primary--text'" depressed @click="goToAppointment">
											<translate v-if="appointment.effective" key="go-to">Enter</translate>
											<translate v-else key="start">Start Appointment</translate>
										</v-btn>
										<v-btn v-if="!appointment.effective && $store.getters.hasRole('receptionist') && !$store.getters.hasRole('manager')" color="secondary" class="primary--text" depressed @click="saveAppointment(false, true)" :disabled="!$store.getters.hasAnyModule">
											<translate key="present">Present</translate>
										</v-btn>
										<v-btn v-if="!appointment.effective && !appointment.missed" color="secondary" class="primary--text" depressed @click="missedOpen = true">
											<translate key="clinic-history">Missed</translate>
										</v-btn>
										<v-btn v-if="edited" color="secondary" class="primary--text" depressed @click="saveAppointment(false, false)" :disabled="!$store.getters.hasAnyModule">
											<translate key="save">Save</translate>
										</v-btn>
									</template>
									<template v-else>
										<template v-if="appointment.google_id">
											<v-btn color="secondary" class="primary--text" depressed @click="currentEvent = appointment; selectedOpen = false;">
												<translate key="associate-to">Associate to</translate>
											</v-btn>
											<v-btn color="secondary" class="primary--text" depressed @click="saveAppointment(false, false)">
												<translate key="schedule-google" translate-context="Verb">Schedule</translate>
											</v-btn>
										</template>
										<template v-else>
											<v-btn v-if="ownAppointment" color="secondary" class="primary--text" depressed @click="saveAppointment(true, false)" :disabled="!$store.getters.hasAnyModule">
												<translate key="schedule-go-to">Start Appointment</translate>
											</v-btn>
											<v-btn :color="tourFillStep ? 'primary' : 'secondary'" :class="tourFillStep ? 'white--text' : 'primary--text'" depressed @click="saveAppointment(false, false)" :disabled="!$store.getters.hasAnyModule">
												<translate key="schedule" translate-context="Verb">Schedule</translate>
											</v-btn>
										</template>
									</template>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</v-sheet>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import NewAppointmentForm from "@/components/appointment/NewAppointmentForm.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import axios from "axios";
import { mapGetters } from 'vuex'

export default {
	name: "Calendar",
	props: ["showNewAppointment"],
	components: {
		Breadcrumbs,
		NewAppointmentForm,
		DeleteConfirmationDialog
	},
	data: () => ({
		showFilters: false,
		userFilter: "",
		establishmentFilter: "",
		healthProfessionals: [],
		specialities: [],
		establishments: [],
		patients: [],
		appointmentTypes: [],
		appointmentLocations: [],
		newApp: false,
		appointment: {
			appointment_id: "",
			patient_id: "",
			speciality_id: "",
			establishment_id: "",
			user_id: "",
			type_id: null,
			location_id: null,
			description: "",
			date: "",
			time: "",
			duration: 15,
			location: "",
			notes: ""
		},
		focus: "",
		now: null,
		type: "week",
		start: null,
		end: null,
		selectedEvent: {},
		selectedElement: null,
		selectedOpen: false,
		appointments: [{ start: "1900-01-01", name: "" }],
		googleEvents: [],
		googleLogging: false,
		showGoogleSettings: false,
		showCalendarUsers: false,
		usedCalendars: [],
		otherCalendars: [],
		usersIn: [],
		usersOut: [],
		edited: false,
		currentEvent: null,
		currentCalendar: null,
		showAutomaticInfo: false,
		showNumAppointments: false,
		numAppointmentsAssociated: null,
		showDeleteAppointment: false,
		missedOpen: false
	}),
	computed: {
		items() {
			return [
				{
					text: this.$gettext("Schedule"),
					disabled: false,
					link: true,
					to: { name: "calendar" }
				}
			];
		},
		required() {
			return value => !!value || this.$gettext("Required.");
		},
		firstInterval() {
			return this.$store.getters.calendarMin * 2;
		},
		intervalCount() {
			return this.$store.getters.calendarMax * 2 - this.firstInterval;
		},
		weekdays() {
			var baseWeekdays = [1, 2, 3, 4, 5];
			if (this.$store.getters.showSaturday) baseWeekdays.push(6);
			if (this.$store.getters.showSunday) baseWeekdays.push(0);
			return baseWeekdays;
		},
		ownAppointment() {
			return this.$store.getters.userId == this.appointment.user_id;
		},
		title() {
			const { start, end } = this;
			if (!start || !end) {
				return "";
			}

			//const startMonth = this.monthFormatter(start);
			//const endMonth = this.monthFormatter(end);
			const startMonth = this.monthFormat(start);
			const endMonth = this.monthFormat(end);
			const suffixMonth = endMonth; //startMonth === endMonth ? "" : endMonth;

			const startYear = start.year;
			const endYear = end.year;
			const suffixYear = endYear; //startYear === endYear ? "" : endYear;

			const startDay = start.day; //+ this.nth(start.day);
			const endDay = end.day; //+ this.nth(end.day);

			switch (this.type) {
				case "month":
					return `${startMonth} ${startYear}`;
				case "week":
				case "4day":
					return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`;
				case "day":
					return `${startMonth} ${startDay} ${startYear}`;
			}
			return "";
		},
		typeToLabel() {
			return {
				month: this.$gettext("Month"),
				week: this.$gettext("Week"),
				day: this.$gettext("Day"),
				"4day": this.$gettext("4 Days")
			};
		},
		monthFormatter() {
			return this.$refs.calendar.getFormatter({
				timeZone: "WET",
				month: "long"
			});
		},
		tourFillStep() {
			return this.$store.getters.tourStepEvent == 'tour-fill-appointment';
		},
		...mapGetters({
      tourStep: 'tourStepEvent'
    })
	},
	mounted() {
		this.$refs.calendar.checkChange();
	},
	methods: {
		filterAppointments(healthProfessional) {
			this.appointments = this.appointments.filter(
				app => app.healthProfessional == healthProfessional.id
			);
		},
		newAppointment(date) {
			if(!this.$store.getters.hasRole('receptionist') && !this.$store.getters.canCreateAppointment) return 0;
			console.log("NEW APP", date);
			this.newApp = true;
			this.appointment = {
				appointment_id: "",
				patient_id: "",
				speciality_id: "",
				establishment_id: "",
				user_id: "",
				type_id: null,
				location_id: null,
				description: "",
				date: "",
				time: "",
				duration: 15,
				location: "",
				notes: ""
			};
			//this.appointment.date = date.date;
			//this.appointment.time = date.time;
			if (this.$refs.AppointmentForm) this.$refs.AppointmentForm.clear();
			this.selectedOpen = true;
			setTimeout(() => {
				this.appointment.date = date.date;
				this.appointment.time = date.time;
				this.appointment.establishment_id = "";
			}, 10);
		},
		goToAppointment() {
			this.$router.push({
				name: "appointment",
				params: {
					pid: this.appointment.patient_id,
					aid: this.appointment.appointment_id
				}
			});
		},
		getForm() {
			const formData = new FormData();
			formData.append("speciality_id", this.appointment.speciality_id);
			formData.append("establishment_id", this.appointment.establishment_id);
			if(this.appointment.patient_id != -1) formData.append("patient_id", this.appointment.patient_id);
			formData.append("user_id", this.appointment.user_id);
			if(this.appointment.type_id) formData.append("type_id", this.appointment.type_id);
			if(this.appointment.location_id) formData.append("location_id", this.appointment.location_id);
			formData.append("description", this.appointment.description);
			formData.append("date", this.appointment.date);
			formData.append("time", this.appointment.time);
			formData.append("duration", this.appointment.duration);
			formData.append("location", this.appointment.location);
			formData.append("notes", this.appointment.notes);
			if(this.appointment.google_id) formData.append("google_id", this.appointment.google_id);
			return formData;
		},
		automaticAssociation() {
			this.googleLogging = true;
			axios
					.post("/google-calendar/auto-association")
					.then(response => {
						if (response.data.result === 'ok') {
							console.log(response.data.num_appointments_associated);
							this.showAutomaticInfo = false;
							this.showGoogleSettings = false;
							this.numAppointmentsAssociated = response.data.num_appointments_associated;
							this.showNumAppointments = true;
							this.googleLogging = false;
							this.getAppointments();
						}
					})
					.catch(error => {
						console.log(error);
						this.googleLogging = false;
					});
		},
		associateAppointment() {
			let formData = new FormData();
			console.log(this.currentEvent.google_id);
			formData.append("appointment_id", this.appointment.appointment_id);
			formData.append("google_id", this.currentEvent.google_id);
			axios
					.post("/api/appointment/associate", formData)
					.then(response => {
						if (response.data.result === 'ok') {
							this.selectedOpen = false;
							this.currentEvent = null;
							this.getAppointments();
						}
					})
					.catch(error => console.log(error));
		},
		saveAppointment(goTo, effectuate, missed = {use: false}) {
			if (this.$refs.AppointmentForm.validate()) {
				let formData = this.$refs.AppointmentForm.getForm();
				var isNewPatient = false;
				if (this.appointment.patient_id == -1) {
					isNewPatient = true;
					var new_name = "";
					this.patients.forEach(v => {
						if (v.id == -1) {
							new_name = v.real_name;
						}
					});
					formData.append("new_name", new_name);
				}
				var link = "";
				if (!this.newApp) {
					link = "/api/appointment/edit";
					formData.append("appointment_id", this.appointment.appointment_id);
				} else {
					link = "/api/appointment/new";
				}
				if(effectuate) link = "/api/appointment/effectuate";
				if(missed.use) {
					link = "/api/appointment/missed";
					formData.append("missed", missed.value)
					formData.append("missed_reason", this.appointment.missed_reason);
				}
				axios
					.post(link, formData)
					.then(response => {
						if (response.data.result === 'ok') {
							this.missedOpen = false;
							this.selectedOpen = false;
							if(isNewPatient) {
								this.patients.pop();
								this.patients.push(response.data.patient);
							}
							if(goTo) {
								this.appointment.appointment_id = response.data.id;
								if(isNewPatient) this.appointment.patient_id = response.data.patient.id;
								this.goToAppointment();
							} else this.getAppointments();
						} else if(response.data.result === 'limited') {
							this.$refs.AppointmentForm.setErrorPlan();
						}
					})
					.catch(error => console.log(error));
			}
		},
		deleteAppointment() {
			const patientId = this.appointment.patient_id;
			axios
				.post(
					"/patient/" +
						patientId +
						"/appointment/" +
						this.appointment.appointment_id +
						"/delete"
				)
				.then(response => {
					console.log(response.data);
					if (response.data.result === "deleted") {
						this.selectedOpen = false;
						this.showDeleteAppointment = false;
						this.getAppointments();
					}
				})
				.catch(error => console.log(error));
		},
		intervalFormat(date) {
			return date.time;
		},
		monthFormat(date) {
			const month = date.month;
			if (month == 1) return this.$gettext("Jan");
			else if (month == 2) return this.$gettext("Feb");
			else if (month == 3) return this.$gettext("Mar");
			else if (month == 4) return this.$gettext("Apr");
			else if (month == 5) return this.$gettext("May");
			else if (month == 6) return this.$gettext("Jun");
			else if (month == 7) return this.$gettext("Jul");
			else if (month == 8) return this.$gettext("Aug");
			else if (month == 9) return this.$gettext("Sep");
			else if (month == 10) return this.$gettext("Oct");
			else if (month == 11) return this.$gettext("Nov");
			else return this.$gettext("Dec");
		},
		weekDayFormat(some) {
			const weekday = some.weekday;
			if (weekday == 1) return this.$gettext("Monday");
			else if (weekday == 2) return this.$gettext("Tuesday");
			else if (weekday == 3) return this.$gettext("Wednesday");
			else if (weekday == 4) return this.$gettext("Thursday");
			else if (weekday == 5) return this.$gettext("Friday");
			else if (weekday == 6) return this.$gettext("Saturday");
			else if (weekday == 0) return this.$gettext("Sunday");
		},
		viewDay({ date }) {
			this.focus = date;
			this.type = "day";
		},
		getEventColor(event) {
			if((this.userFilter != 'all' && this.establishmentFilter == 'all') || (this.userFilter == 'all' && this.establishmentFilter == 'all' && this.healthProfessionals.length == 1 && this.establishments.length > 1)) {
				let index = this.establishments.findIndex(e => event.establishment_id == e.id);
				if(index != -1) return this.establishments[index].color;
			}
			return event.color;
		},
		getEventName(event) {
			console.log(event);
			return event.input.name;
		},
		setToday() {
			this.focus = this.today;
		},
		prev() {
			this.$refs.calendar.prev();
		},
		next() {
			this.$refs.calendar.next();
		},
		showAppointment({ nativeEvent, event }) {
			console.log("OPEN APPOINTMENT");
			this.edited = false;
			if(!event.google) this.newApp = false;
			else this.newApp = true;
			const open = () => {
				console.log("OPEN");
				this.selectedEvent = event;
				this.selectedElement = nativeEvent.target;
				setTimeout(() => {
					this.selectedOpen = true;
					this.appointment = event;
				}, 10);
			};
			open();
			nativeEvent.stopPropagation();
		},
		updateRange({ start, end }) {
			this.start = start;
			this.end = end;
			if (this.userFilter) {
				this.getAppointments();
			}
		},
		nth(d) {
			return d > 3 && d < 21
				? "th"
				: ["th", "st", "nd", "rd", "th", "th", "th", "th", "th", "th"][d % 10];
		},
		rnd(a, b) {
			return Math.floor((b - a + 1) * Math.random()) + a;
		},
		formatDate(a, withTime) {
			return withTime
				? `${a.getFullYear()}-${a.getMonth() +
						1}-${a.getDate()} ${a.getHours()}:${a.getMinutes()}`
				: `${a.getFullYear()}-${a.getMonth() + 1}-${a.getDate()}`;
		},
		getAppointments() {
			axios
				.get("/api/calendar/feed", {
					params: {
						user_filter: this.userFilter,
						establishment_filter: this.establishmentFilter,
						start: this.start.date,
						end: this.end.date + " 23:59:59"
					}
				})
				.then(response => {
					if (response.status === 200) {
						console.log(response.data);
						this.appointments = response.data.events;
						this.googleEvents = response.data.google_events;
					}
				})
				.catch(error => console.log(error));
		},
		loginGoogle() {
			this.googleLogging = true;
			axios
				.get("/google-calendar/login")
				.then(response => {
					this.googleLogging = false;
					window.location.href = response.data.authorization_url;
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		logoutGoogle() {
			this.googleLogging = true;
			axios
				.get("/google-calendar/logout")
				.then(response => {
					this.googleLogging = false;
					if(response.data.result == "ok") {
						this.showGoogleSettings = false;
						this.$store.dispatch('SET_GOOGLE_LOGGED', false);
						this.getAppointments();
					}
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		getGoogleCalendars() {
			this.googleLogging = true;
			axios
				.get("/google-calendar/calendars")
				.then(response => {
					console.log(response.data);
					this.usedCalendars = response.data.used_calendars;
					this.otherCalendars = response.data.other_calendars;
					this.googleLogging = false;
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		addCalendar(calendar) {
			this.googleLogging = true;
			calendar['user_id'] = this.$store.getters.userId;
			axios
				.post("/google-calendar/calendars/add", calendar)
				.then(response => {
					console.log(response.data);
					this.otherCalendars.splice(this.otherCalendars.indexOf(calendar), 1);
					this.usedCalendars.push(response.data.calendar);
					this.googleLogging = false;
					this.getAppointments();
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		removeCalendar(calendar) {
			this.googleLogging = true;
			axios
				.post("/google-calendar/calendars/remove", calendar)
				.then(response => {
					console.log(response.data);
					this.usedCalendars.splice(this.usedCalendars.indexOf(calendar), 1);
					this.otherCalendars.push(calendar);
					this.googleLogging = false;
					this.getAppointments();
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		saveTerapeut() {
			this.googleLogging = true;
			axios
				.post("/google-calendar/calendars/user", 
					{
						calendar_id: this.currentCalendar.id,
						terapeut_id: this.currentCalendar.user_id
					})
				.then(response => {
					console.log(response.data);
					this.googleLogging = false;
					this.showCalendarUsers = false;
					this.getAppointments();
				})
				.catch(error => {
					console.log(error);
					this.googleLogging = false;
				});
		},
		openNewAppointment() {
			const now = new Date();
      var mm = now.getMonth() + 1;
      if(mm.toString().length < 2) mm = "0" + mm;
			var dd = now.getDate();
			var yy = now.getFullYear();
			const info = {};
			info.date = yy + "-" + mm + "-" + dd;
			info.time = now.toLocaleTimeString('en-GB', {
				hour: "2-digit",
				minute: "2-digit"
			});
			this.newAppointment(info);
		}
	},
	created() {
		let code = this.$route.query.code;
		let state = this.$route.query.state;
		if(code && state) console.log(code, state);
		axios
			.get("/calendar", { params: { code: code, state: state } })
			.then(response => {
				if (response.status === 200) {
					console.log(response.data);
					this.healthProfessionals = response.data.terapeuts;
					this.patients = response.data.patients;
					this.specialities = response.data.specialities;
					this.establishments = response.data.establishments;
					this.appointmentTypes = response.data.appointment_types;
					this.appointmentLocations = response.data.appointment_locations;
					this.userFilter = response.data.user_filter;
					this.establishmentFilter = response.data.establishment_filter;
					if(code && state){
						this.$store.dispatch('SET_GOOGLE_LOGGED', true);
						this.getGoogleCalendars();
						this.showGoogleSettings = true;
					}
					this.getAppointments();
					if(this.showNewAppointment) this.openNewAppointment();
				}
			})
			.catch(error => console.log(error));
	},
	watch: {
		tourStep(value) {
			if(value == 'tour-fill-appointment') {
				let patient_id = -1;
				for(let i=0; i < this.patients.length; i++) {
					if(this.patients[i].name == 'Alberto Caeiro') {
						patient_id = this.patients[i].id;
						break;
					}
				}
				this.appointment.patient_id = patient_id;
				if(patient_id == -1) this.patients.push({id: -1, name: 'Alberto Caeiro (Novo Paciente)', real_name: 'Alberto Caeiro'});
				this.appointment.speciality_id = this.specialities[0].id;
				this.appointment.user_id = this.healthProfessionals[0].id;
				this.appointment.duration = 30;
				this.appointment.establishment_id = this.establishments[0].id;
			}
			else if(value == 'tour-create-appointment') {
				this.saveAppointment(false, false);
			}
			else if(value == 'tour-open-appointment') {
				let events = document.getElementsByClassName("v-event-timed");
				console.log(events);
				for(let i = events.length - 1; i >= 0; i--) {
					if(events[i].innerText == 'Alberto C.') {events[i].click(); break;}
				}
			}
			else if(value == 'tour-go-to-appointment') {
				this.goToAppointment();
			}
		}
	}
};
</script>

<style scoped>
>>> .v-calendar-daily__intervals-body {
	border-bottom: #e0e0e0 1px solid;
}

>>> .v-calendar-daily__day.v-present {
	background-color: var(--v-secondary-base);
}

>>> .v-btn--fab.v-size--default {
    height: 40px;
    width: 40px;
}
</style>